import { Model } from "@ofqual/portal-core/core/types";

type NullableString = string | null;
type NullableBoolean = boolean | null;
type NullableNumber = number | null;
type NullableDateTime =  Date |null;


type SocItemCore = {
  id: string;
  createdDate : Date;
  modifiedDate : Date;
  createdByUPN : string;
  createdByDisplayName : string; 
  modifiedByUPN : string;
  modifiedByDisplayName : string;
  lastResponseByUPN : string;
  lastResponseByDisplayName : string;
  lastResponseDate : NullableString;
  organisationId: number;
  orgName: NullableString;
  status : number; 
  caseTitle : string; 
  referenceNumber : string; 
  dateSubmitted : NullableString;
  regulatorsToNotifyOfqual : boolean; 
  regulatorsToNotifyCCEA : boolean; 
  regulatorsToNotifyQW : boolean; 
  currentCompliant : NullableBoolean; 
  currentNonComplianceDescription : NullableString;
  currentNonComplianceNonOfqualConditions : NullableString;
  currentNonComplianceMitigation : NullableString ;
  currentCompliantDate : NullableDateTime;
  futureCompliant : NullableBoolean;
  futureNonComplianceNonOfqualConditions : string;
  futureNonComplianceDescription :NullableString;
  futureNonComplianceHow : NullableString;
  futureNonComplianceMitigation : NullableString;
  fullAccountsUpload: SocOrganisationStability | null;
  fullAccountsUploadInformation : string;
  financialPerformance2023Information :string ;
  totalRevenue : NullableNumber;
  totalExpenditure : NullableNumber;
  netProfit : NullableNumber;
  cashInHand : NullableNumber;
  forecastAssumptionsInformation : string ;
  cashReservesPolicy : NullableBoolean;
  cashReservesPolicyInformation : string ;
  cashReservesPolicyMet : CashReservesPolicyMet | null;
  cashReservesPolicyMetInformation : string;
  annualFixedCosts : NullableNumber ;
  cashReservesOverrideInformation : string;
  fundingSources : NullableBoolean;
  fundingSourcesInformation : string;
  customerRisksIdentifiedInformation : string;
  customerRisksManagedInformation : string;
  financialRisksInformation : string;
  malpracticeGovernanceInformation : string ; 
  malpracticeOperationalInformation : string;
  malpracticeResourceInformation : string;
  decisionMakingRegulatorInformation : string;
  decisionMakingAOAffectedInformation :  string;
  decisionMakingAONotifyInformation :string;
  intelligenceInformation : string; 
  aiRisksInformation : string;
  aiSystems : string;
  aiCentres : string;
  qualificationExpertiseTypeInformation : string;
  qualificationExpertiseAvailabilityInformation : string;
  qualificationExpertiseExpansionInformation : string;
  reviewProcessExpertiseInformation : string; 
  reviewProcessMonitoringInformation : string;
  expertiseAcquisitionInformation : NullableString;
  govBodyConfirmCompliance : NullableBoolean;
  govBodyNoCompliance : NullableString ;
  govBodyConfirmApproval : NullableBoolean;
  govBodyConfirmNoApproval : NullableString;
  govBodyConfirmChairRo : NullableBoolean;
  govBodyConfirmNoChairRo : NullableString;
  govBodyConfirmNameRo : NullableString; 
  govBodyConfirmNameChair : NullableString; 
  govBodyConfirmSigningOnBehalfName : NullableString; 
  govBodyConfirmSigningOnBehalfTitle : NullableString; 
  socCurrentNonComplianceGeneralConditions: number[];
  socCurrentNonComplianceQualConditions: number[];
  socCurrentNonComplianceSubjectConditions: number[];
  socFutureNonComplianceGeneralConditions: number[];
  socFutureNonComplianceQualConditions: number[];
  socFutureNonComplianceSubjectConditions: number[];
};

type SocItemOfqual = {
  assignedToUpn: string;
  assignedToDisplayName: string;
  agreeCurrentCompliant: NullableBoolean;  
  agreeFutureCompliant: NullableBoolean; 
  complianceDisagreement: NullableNumber; 
  complianceDisagreementDescription: string; 
  complianceRagRating:  NullableNumber; 
  complianceFollowUp: NullableNumber; 
  complianceFollowUpStatus: NullableNumber; 
  complianceClosureStatus: NullableNumber;
  kloeOneRagRating: NullableNumber; 
  kloeOneFollowUp: NullableNumber; 
  kloeOneFollowUpStatus: NullableNumber; 
  kloeOneLead: NullableString; 
  kloeOneClosureStatus: NullableNumber;
  kloeTwoRagRating: NullableNumber; 
  kloeTwoFollowUp: NullableNumber; 
  kloeTwoFollowUpStatus: NullableNumber; 
  kloeTwoLead: NullableString;  
  kloeTwoClosureStatus: NullableNumber;
  kloeThreeRagRating: NullableNumber;
  kloeThreeFollowUp: NullableNumber; 
  kloeThreeFollowUpStatus: NullableNumber; 
  kloeThreeLead: string; 
  kloeThreeClosureStatus: NullableNumber;
  overallRagRating: NullableNumber; 
  followUpLead: NullableNumber; 
  closingStatus: NullableNumber;
  closingStatusRationale: NullableNumber;
  closeReason: NullableString;
};

export const defaultValues: Partial<SocItem> = {
  regulatorsToNotifyOfqual: false,
  regulatorsToNotifyCCEA: false,
  regulatorsToNotifyQW: false,
  currentCompliant: null,
  currentNonComplianceDescription: null,
  currentNonComplianceMitigation: null,
  currentCompliantDate: null,
  futureCompliant: null,
  futureNonComplianceDescription: null,
  futureNonComplianceHow: null,
  socCurrentNonComplianceGeneralConditions: [],
  socCurrentNonComplianceQualConditions: [],
  socCurrentNonComplianceSubjectConditions: [],
  socFutureNonComplianceGeneralConditions: [],
  socFutureNonComplianceQualConditions: [],
  socFutureNonComplianceSubjectConditions: [],
  totalRevenue: null,
  totalExpenditure: null,
  govBodyConfirmCompliance: null,
  govBodyNoCompliance: null,
  govBodyConfirmApproval: null,
  govBodyConfirmNoApproval: null,
  govBodyConfirmChairRo: null,
  govBodyConfirmNoChairRo: null,
  govBodyConfirmNameRo: null,
  govBodyConfirmNameChair: null,
  govBodyConfirmSigningOnBehalfName: null,
  govBodyConfirmSigningOnBehalfTitle: null,
  closeReason: null,
  
};

export type SocItem = SocItemCore & SocItemOfqual;

export const requiredOfqualFields: (keyof SocItemOfqual)[] = [
  "agreeCurrentCompliant",
  "agreeFutureCompliant",
  "complianceRagRating",
  "complianceFollowUp",
  "complianceFollowUpStatus", 
  "complianceClosureStatus",
  "kloeOneRagRating",
  "kloeOneFollowUp",
  "kloeOneFollowUpStatus",
  "kloeOneLead",
  "kloeOneClosureStatus",
  "kloeTwoRagRating",
  "kloeTwoFollowUp",
  "kloeTwoFollowUpStatus",
  "kloeTwoLead",
  "kloeTwoClosureStatus",
  "kloeThreeRagRating",
  "kloeThreeFollowUp",
  "kloeThreeFollowUpStatus",
  "kloeThreeLead",
  "kloeThreeClosureStatus",
  "overallRagRating",
  "closingStatus",
  "closingStatusRationale",
  "closeReason",
 
];

export const ofqualKeys: (keyof SocItemOfqual)[] = [
  ...requiredOfqualFields,
  "kloeThreeRagRating",
  "kloeThreeFollowUp",
  "kloeThreeFollowUpStatus",
  "kloeThreeLead",
];

export enum SocStatus {
  New = 0,
  Draft = 1,
  Submitted = 2,
  InReview = 3,
  ReviewMeetingHeld = 4,
  Closed = 5,
}

export enum SocOrganisationStability {
  fullAccountsUploadYes = 1,
  fullAccountsUploadNo3months = 0,
  fullAccountsUploadNoCircumstances = 2
}

export enum CashReservesPolicyMet {
  cashReservesPolicyMetYes = 1,
  cashReservesPolicyMetNo = 0,
  cashReservesPolicyMetNoReservesPolicy = 2
}

export const fromApiModel = (model: Model): SocItem => {
  return {
    ...model,
  } as SocItem;
};

export const toApiModel = (item: SocItem): Model => {
  return {
    ...item,
  } as Model;
};

export enum PlanToBuildReserveStatus {
  Yes = 1,
  No = 2,
  "N/A" = 3,
}
